import {memo} from 'react';

import {useDispatch} from 'react-redux';
import styled from 'styled-components';

import {getLocalizationService} from '~/shared/services/localisationService';
import actions from '~/shared/store/actions';
import {media} from '~/shared/theme/utils';
import {trackEvent} from '~/shared/services/analytics';
import {SubPageTitle, SharedCheckoutCloseButton} from '~/shared/components/styled';
import ImageWithAlt from '~/shared/components/ImageWithAlt';
import {LayoutWithHeader} from '~/shared/components/LayoutWithHeader';
import {AddCreditCardModes} from '~/shared/consts/checkoutConsts';
import {PaymentMethodTypes} from '~/shared/consts/paymentConsts';
import pciLogoUrl from '~/assets/images/pci-logo.png';
import {FlexColumn, flexColumn} from '~/shared/theme/FlexLayout';
import {body12Normal} from '~/shared/theme/typography';
import {handleRefreshToken} from '~/shared/services/auth';
import {Payment} from '~/shared/store/models';
import {isAvailablePayments} from '~/shared/utils/payments/payments';
import {is401Error} from '~/shared/services/apiErrorService';
import {MINIMAL_IFRAME_HEIGHT_PX} from '~/shared/components/AddCreditCard/consts';

import Button from '../Button';

import AddCreditCardIFrame from './AddCreditCardIFrame';

const CreditCardWrapper = styled(FlexColumn)<{extendCreditCardFormWidth?: boolean}>`
  width: 100%;
  min-height: ${MINIMAL_IFRAME_HEIGHT_PX}px;
  ${media.minMobile`
    width: 500px;
    margin: 0px auto;
  `}
  ${({extendCreditCardFormWidth}) => extendCreditCardFormWidth && media.minTablet`
    width: 518px;
  `}
`;

const MinimalBlockWrapper = styled(FlexColumn)`
  max-width: 500px;
  min-height: ${MINIMAL_IFRAME_HEIGHT_PX}px;
  margin: 10px auto;
`;

const CloseButton = styled(Button)`
  width: 18px;
  height: 18px;
  margin: 10px 0;
`;

const StyledCloseSvg = styled(SharedCheckoutCloseButton)`
  position: inherit;
`;

const SecurityComment = styled.div`
  align-self: center;
  ${body12Normal}
  margin-top: 8px;
  color: ${({theme}) => theme.colors.text};
`;

const PciLogo = styled(ImageWithAlt).attrs({
  src: pciLogoUrl,
})`
  width: 100px;
  margin: 15px auto 0 auto;
`;

const OneLineContainer = styled.div`
  ${flexColumn}
  flex: 1;
`;

const OneLineModeBlock = ({children, onClose}: {children?: React.ReactNode; onClose?: () => void}) => {
  const {t} = getLocalizationService();

  return (
    <OneLineContainer>
      <CloseButton aria-label={t('close')} onClick={onClose}>
        <StyledCloseSvg />
      </CloseButton>
      {children}
      <SecurityComment>{t('security_comment')}</SecurityComment>
      <PciLogo alt="Pci logo" />
    </OneLineContainer>
  );
};

const DefaultModeBlock = ({children, onBackLinkClick, openCheckout, withoutHeader}: {
  children?: React.ReactNode;
  onBackLinkClick?: () => void;
  openCheckout?: (() => void) | null;
  withoutHeader?: boolean;
}) => {
  const onBackClick = onBackLinkClick || openCheckout;

  if (!withoutHeader) {
    return (
      <LayoutWithHeader onClick={onBackClick} titleKey="add_credit_card" showBackArrow titleComponent={SubPageTitle}>
        <CreditCardWrapper>
          {children}
        </CreditCardWrapper>
      </LayoutWithHeader>
    );
  }

  return (
    <CreditCardWrapper extendCreditCardFormWidth>{children}</CreditCardWrapper>
  );
};

const MinimalModeBlock = ({children}: {children?: React.ReactNode}) => (
  <MinimalBlockWrapper>
    {children}
  </MinimalBlockWrapper>
);

const AddCreditCard = ({
  onSuccess,
  onFailure,
  onClose,
  onBackLinkClick,
  openCheckout = null,
  mode = null,
  withoutHeader,
  minimalWrapper,
  withContrastFilter,
}: {
  onSuccess?: (newCreditCard?: Payment) => void;
  onFailure?: (error: unknown) => void;
  onClose?: () => void;
  onBackLinkClick?: () => void;
  openCheckout?: (() => void) | null;
  mode?: AddCreditCardModes | null;
  withoutHeader?: boolean;
  minimalWrapper?: boolean;
  withContrastFilter?: boolean;
}) => {
  const dispatch = useDispatch();
  const handleSuccess = async (data: {
    last4Digits: string;
  }) => {
    try {
      const payments = await dispatch(actions.getAvailablePayments());

      if (!isAvailablePayments(payments)) {
        return;
      }

      const availablePayment = payments?.availablePayments?.reduce<Payment | undefined>((bestMatchingCard, card) => {
        const isMatchingPayment = card.cardLastDigits === data.last4Digits && card.paymentMethod === PaymentMethodTypes.CREDIT_CARD;

        return isMatchingPayment && (bestMatchingCard?.cardId || 0) < card.cardId ? card : bestMatchingCard;
      }, undefined);

      if (availablePayment) {
        dispatch(actions.addPayment({...availablePayment, assigned: true, isDisabled: false}));
      }
      trackEvent('hasSuccessfullyAddedCreditCard');
      if (onSuccess) {
        onSuccess(availablePayment);
      }
    } catch (e) {
      if (is401Error(e)) {
        await handleRefreshToken(e, handleSuccess, data);
        return;
      }
      if (onFailure) {
        onFailure(e);
      }
    }
  };

  const BlockComp = mode === AddCreditCardModes.ONE_LINE ? OneLineModeBlock
    : minimalWrapper ? MinimalModeBlock
      : DefaultModeBlock;
  return (
    <BlockComp
      onClose={onClose}
      onBackLinkClick={onBackLinkClick}
      openCheckout={openCheckout}
      withoutHeader={withoutHeader}
    >
      <AddCreditCardIFrame onSuccess={handleSuccess} mode={mode} withContrastFilter={withContrastFilter} />
    </BlockComp>
  );
};

export default memo(AddCreditCard);
